import React, { useEffect } from "react";
import { startOfMonth, endOfMonth, format } from "date-fns";
import styles from "./style.module.scss";
import ButtonIcon from "../../../../components/ButtonIcon/ButtonIcon";
import Avatar from "../../../../components/Avatar/Avatar";
import GoldMedal from "../../../../../../images/v2/GoldAward.png";
import SilverMedal from "../../../../../../images/v2/SilverAward.png";
import BronzeMedal from "../../../../../../images/v2/BronzeAward.png";
import { fetchGoalData } from "../../../../stores/dashboard";
import SetDailyGoalModal from "../set-daily-goal-modal";
import useDashboardStore from "../../../../stores/dashboard";
import useUserStore from "../../../../stores/user";
import useDomainStore from "../../../../stores/domain";
import { dateFormatter } from "../../../../../components/helper/helper";
import SkeletonLoader from "../../../../components/skeleton-loader";
import SettingsSlidersIcon16 from "../../../../assets/svg/icons/common/SettingsSlidersIcon16";

const MonthlyGoals = () => {
  const currentMonthStartDate = format(startOfMonth(new Date()), "dd MMM");
  const currentMonthEndDate = format(endOfMonth(new Date()), "dd MMM");
  const goalDataLoading = useDashboardStore((state) => state.goalDataLoading);
  const goalData = useDashboardStore((state) => state.goalData);
  const setDailyGoalModalOpen = useDashboardStore(
    (state) => state.setDailyGoalModalOpen,
  );
  const goalFilterKey = useDashboardStore((s) => s.filterData.goalFilterKey);
  const isAdmin = useUserStore((s) => s.isAdmin);
  const organizationId = useUserStore((s) => s.organizationId);
  const { isKurvDomain } = useDomainStore();

  const getInitials = (name) => {
    if (!name) return "";
    const nameParts = name.split(" ");
    if (nameParts.length === 1) {
      return nameParts[0].substring(0, 2).toUpperCase();
    }
    return (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase();
  };

  const getMedal = (index) => {
    if (index === 0) return GoldMedal;
    if (index === 1) return SilverMedal;
    return BronzeMedal;
  };

  useEffect(() => {
    const abortController = new AbortController();
    fetchGoalData(
      goalFilterKey
        ? { user: goalFilterKey }
        : isAdmin
          ? { user: `org-${organizationId}` }
          : {},
      abortController,
    );

    return () => {
      abortController.abort();
    };
  }, [goalFilterKey, isAdmin, organizationId]);

  return (
    <div className={`${styles.monthlyGoals} ${isKurvDomain ? styles.kurv_theme : ""}`}>
      <div className={styles.monthlyGoalsHeader}>
        <div className={styles.monthlyGoalsHeaderLeft}>
          <h2 className={styles.monthlyGoalsTitle}>Monthly Goals</h2>
          <p className={styles.monthlyGoalsDescription}>
            {new Date(currentMonthStartDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} - {new Date(currentMonthEndDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
          </p>
        </div>
        <div className={styles.monthlyGoalsHeaderRight}>
          {goalDataLoading ? (
            <SkeletonLoader
              variant="rectangular"
              height={"32px"}
              width={"48px"}
            />
          ) : (
            <ButtonIcon
              bgColor="#FFF"
              onClick={() => setDailyGoalModalOpen(true)}
            >
              <SettingsSlidersIcon16 />
            </ButtonIcon>
          )}
        </div>
      </div>

      {goalDataLoading && (
        <div className={styles.progressList}>
          {[1, 2, 3].map((item) => (
            <div className={styles.progressItem} key={`progress-item-${item}`}>
              <div className={styles.progressItemHeader}>
                <div className={styles.progressItemHeaderLeft}>
                  <SkeletonLoader
                    variant="text"
                    height={"24px"}
                    width={"124px"}
                  />
                </div>

                <div className={styles.progressItemHeaderRight}>
                  <SkeletonLoader
                    variant="text"
                    height={"24px"}
                    width={"50px"}
                  />
                </div>
              </div>

              <div className={styles.progressContainer}>
                <div className={styles.progressBar}>
                  <SkeletonLoader
                    variant="rectangular"
                    height={"7px"}
                    width={"100%"}
                  />
                </div>
              </div>

              <div className={styles.progressItemFooter}>
                <SkeletonLoader
                  variant="text"
                  height={"16px"}
                  width={item == 3 ? "200px" : "100px"}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {!goalDataLoading && (
        <div className={styles.progressList}>
          <div className={styles.progressItem}>
            <div className={styles.progressItemHeader}>
              <div className={styles.progressItemHeaderLeft}>
                <p>Submitted apps</p>
              </div>

              <div className={styles.progressItemHeaderRight}>
                <p>{goalData?.submittedApps?.perc}%</p>
              </div>
            </div>

            <div className={styles.progressContainer}>
              <div className={styles.progressBar}>
                <div
                  className={styles.progressBarFill}
                  style={{
                    width: `${Math.min(goalData?.submittedApps?.perc, 100).toFixed(2)}%`,
                  }}
                ></div>
              </div>
            </div>

            <div className={styles.progressItemFooter}>
              <p>
                {goalData?.submittedApps?.achieved}/
                {goalData?.submittedApps?.target || "Not set"}
              </p>
            </div>
          </div>

          <div className={styles.progressItem}>
            <div className={styles.progressItemHeader}>
              <div className={styles.progressItemHeaderLeft}>
                <p>Approved apps</p>
              </div>

              <div className={styles.progressItemHeaderRight}>
                <p>{goalData?.approvedApps?.perc}%</p>
              </div>
            </div>

            <div className={styles.progressContainer}>
              <div className={styles.progressBar}>
                <div
                  className={styles.progressBarFill}
                  style={{
                    width: `${Math.min(goalData?.approvedApps?.perc, 100).toFixed(2)}%`,
                  }}
                ></div>
              </div>
            </div>

            <div className={styles.progressItemFooter}>
              <p>
                {goalData?.approvedApps?.achieved}/
                {goalData?.approvedApps?.target || "Not set"}
              </p>
            </div>
          </div>

          <div className={styles.progressItem}>
            <div className={styles.progressItemHeader}>
              <div className={styles.progressItemHeaderLeft}>
                <p>Monthly processing volume</p>
              </div>

              <div className={styles.progressItemHeaderRight}>
                <p>{goalData?.monthlyVolume?.perc}%</p>
              </div>
            </div>

            <div className={styles.progressContainer}>
              <div className={styles.progressBar}>
                <div
                  className={styles.progressBarFill}
                  style={{
                    width: `${Math.min(goalData?.monthlyVolume?.perc, 100).toFixed(2)}%`,
                  }}
                ></div>
              </div>
            </div>

            <div className={styles.progressItemFooter}>
              <p>
                $ {goalData?.monthlyVolume?.achieved}/ $
                {goalData?.monthlyVolume?.totalTarget || "Not set"}
              </p>
            </div>
          </div>
        </div>
      )}

      {goalDataLoading && (
        <SkeletonLoader
          variant="rectangular"
          style={{
            borderRadius: "16px",
          }}
          height={"103px"}
          width={"100%"}
        />
      )}

      {!goalDataLoading && (
        <div className={styles.topPerformers}>
          <div className={styles.topPerformersHeader}>
            <h2 className={styles.topPerformersTitle}>
              Top performers of last month{" "}
            </h2>
          </div>

          <div className={styles.topPerformersLists}>
            {goalData?.topPerformers?.map((performer, index) => (
              <div key={index} className={styles.topPerformersList}>
                <div className={styles.topPerformersListItem}>
                  <div className={styles.avatarContainer}>
                    <Avatar name={getInitials(performer?.name)} size="medium" />
                    <img
                      src={getMedal(index)}
                      alt="Gold Medal"
                      className={styles.medal}
                    />
                  </div>
                  <div className={styles.topPerformersListItemName}>
                    <p>{performer?.name}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <SetDailyGoalModal />
    </div>
  );
};

export default MonthlyGoals;
